import { Card, Col, DatePicker, List, Row, Select, Table, Tag, Typography } from '@pankod/refine-antd';
import { IResourceComponentsProps } from '@pankod/refine-core';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PAYMENT_PROVIDERS } from 'constants/paymentProviders';
import {
  IPosPaymentChannelReportResponse,
  getPosPaymentChannelReport,
} from 'services/report';
import MetaData from 'utilities/metadata';
import { formatCurrency } from 'utilities/shared';

dayjs.extend(utc);

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

export const PosPaymentChannelsList: React.FC<IResourceComponentsProps> = () => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month').startOf('day'),
    dayjs().endOf('day'),
  ]);
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [reportData, setReportData] = useState<IPosPaymentChannelReportResponse | null>(null);

  const [start, end] = dateRange;

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPosPaymentChannelReport({
        startDate: start ? start.startOf('day').utc().toISOString() : null,
        endDate: end ? end.endOf('day').utc().toISOString() : null,
        channel: selectedChannel || null,
        provider: selectedProvider || null,
        page,
        limit,
      });
      setReportData(res);
    } catch (err) {
      console.error('Failed to fetch POS payment channel report', err);
    } finally {
      setLoading(false);
    }
  }, [start, end, selectedChannel, selectedProvider, page, limit]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const channelMap = useMemo(() => {
    const map: Record<string, { count: number; totalAmount: number; pctOfTotal: number }> = {
      MFS: { count: 0, totalAmount: 0, pctOfTotal: 0 },
      BANK: { count: 0, totalAmount: 0, pctOfTotal: 0 },
      CARD: { count: 0, totalAmount: 0, pctOfTotal: 0 },
      CASH: { count: 0, totalAmount: 0, pctOfTotal: 0 },
    };
    if (reportData?.byChannel) {
      reportData.byChannel.forEach((ch) => {
        const key = ch.channel ? ch.channel.toUpperCase() : 'CASH';
        map[key] = {
          count: ch.count,
          totalAmount: ch.totalAmount,
          pctOfTotal: ch.pctOfTotal,
        };
      });
    }
    return map;
  }, [reportData]);

  const detailColumns = [
    {
      title: 'Date & Time',
      dataIndex: 'salesDate',
      key: 'salesDate',
      render: (val: string) => dayjs(val).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'Sale ID',
      dataIndex: 'salesCopyId',
      key: 'salesCopyId',
      render: (val: string) => <Text code>{val}</Text>,
    },
    {
      title: 'Client',
      dataIndex: 'clientName',
      key: 'clientName',
    },
    {
      title: 'Warehouse',
      dataIndex: 'warehouseName',
      key: 'warehouseName',
    },
    {
      title: 'Channel',
      dataIndex: 'paymentChannel',
      key: 'paymentChannel',
      render: (ch: string) => {
        const channel = ch ? ch.toUpperCase() : 'CASH';
        let color = 'blue';
        if (channel === 'MFS') color = 'magenta';
        if (channel === 'BANK') color = 'purple';
        if (channel === 'CARD') color = 'orange';
        if (channel === 'CASH') color = 'green';
        return <Tag color={color}>{channel}</Tag>;
      },
    },
    {
      title: 'Provider',
      dataIndex: 'paymentProvider',
      key: 'paymentProvider',
      render: (p: string) => p || 'Cash',
    },
    {
      title: 'Reference / Txn ID',
      dataIndex: 'paymentDetails',
      key: 'paymentDetails',
      render: (text: string) => text || '—',
    },
    {
      title: 'Amount',
      dataIndex: 'currentPaidAmount',
      key: 'currentPaidAmount',
      align: 'right' as const,
      render: (amt: number) => <Text strong>{formatCurrency(amt || 0)}</Text>,
    },
  ];

  return (
    <List title={<Title level={3}>POS Payment Channel Report</Title>}>
      <MetaData title="POS Payment Channel Report" />

      {/* Filter controls */}
      <Card style={{ marginBottom: '1.5rem', borderRadius: '8px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Text style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
              Date Range
            </Text>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                  setPage(1);
                }
              }}
            />
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Text style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
              Channel Filter
            </Text>
            <Select
              allowClear
              placeholder="All Channels"
              style={{ width: '100%' }}
              value={selectedChannel}
              onChange={(val) => {
                setSelectedChannel(val);
                setPage(1);
              }}
            >
              <Select.Option value="MFS">MFS (Mobile Financial Service)</Select.Option>
              <Select.Option value="BANK">Bank</Select.Option>
              <Select.Option value="CARD">Card</Select.Option>
              <Select.Option value="CASH">Cash</Select.Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Text style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
              Provider Filter
            </Text>
            <Select
              showSearch
              allowClear
              placeholder="All Providers (e.g. bKash, BRAC Bank)"
              style={{ width: '100%' }}
              value={selectedProvider}
              onChange={(val) => {
                setSelectedProvider(val);
                setPage(1);
              }}
              filterOption={(input, option) =>
                (option?.value as string || '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {PAYMENT_PROVIDERS.map((p) => (
                <Select.Option key={p.name} value={p.name}>
                  {p.name} ({p.channel})
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '1.5rem' }}>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ background: '#FFF0F6', borderRadius: '8px' }}>
            <Text type="secondary">MFS Payments</Text>
            <Title level={3} style={{ margin: '8px 0 4px 0', color: '#C41D7F' }}>
              {formatCurrency(channelMap.MFS.totalAmount)}
            </Title>
            <Text type="secondary">
              {channelMap.MFS.count} txns ({channelMap.MFS.pctOfTotal}%)
            </Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ background: '#F9F0FF', borderRadius: '8px' }}>
            <Text type="secondary">Bank Payments</Text>
            <Title level={3} style={{ margin: '8px 0 4px 0', color: '#531DAB' }}>
              {formatCurrency(channelMap.BANK.totalAmount)}
            </Title>
            <Text type="secondary">
              {channelMap.BANK.count} txns ({channelMap.BANK.pctOfTotal}%)
            </Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ background: '#FFF7E6', borderRadius: '8px' }}>
            <Text type="secondary">Card Payments</Text>
            <Title level={3} style={{ margin: '8px 0 4px 0', color: '#D46B08' }}>
              {formatCurrency(channelMap.CARD.totalAmount)}
            </Title>
            <Text type="secondary">
              {channelMap.CARD.count} txns ({channelMap.CARD.pctOfTotal}%)
            </Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ background: '#F6FFED', borderRadius: '8px' }}>
            <Text type="secondary">Cash Payments</Text>
            <Title level={3} style={{ margin: '8px 0 4px 0', color: '#389E0D' }}>
              {formatCurrency(channelMap.CASH.totalAmount)}
            </Title>
            <Text type="secondary">
              {channelMap.CASH.count} txns ({channelMap.CASH.pctOfTotal}%)
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Provider Breakdown */}
      {reportData?.byProvider && reportData.byProvider.length > 0 && (
        <Card title="Provider Breakdown" style={{ marginBottom: '1.5rem', borderRadius: '8px' }}>
          <Row gutter={[16, 12]}>
            {reportData.byProvider.map((prov, i) => (
              <Col xs={24} sm={12} md={8} lg={6} key={i}>
                <div
                  style={{
                    padding: '12px 16px',
                    border: '1px solid #F0F0F0',
                    borderRadius: '6px',
                    background: '#FAFAFA',
                  }}
                >
                  <Text strong style={{ fontSize: '15px' }}>
                    {prov.provider}
                  </Text>
                  <Tag color="blue" style={{ marginLeft: '8px' }}>
                    {prov.channel}
                  </Tag>
                  <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'space-between' }}>
                    <Text type="secondary">{prov.count} txns</Text>
                    <Text strong>{formatCurrency(prov.totalAmount)}</Text>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* Detail Transactions Table */}
      <Card title="POS Transaction Logs" style={{ borderRadius: '8px' }}>
        <Table
          rowKey="id"
          loading={loading}
          columns={detailColumns}
          dataSource={reportData?.details || []}
          pagination={{
            current: page,
            pageSize: limit,
            total: reportData?.pagination?.total || 0,
            onChange: (p, s) => {
              setPage(p);
              if (s) setLimit(s);
            },
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Card>
    </List>
  );
};
